/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/ban-types */
import MaterialTable, {
  Action,
  Column,
  MaterialTableProps,
  MTableAction,
} from "@material-table/core";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, Fab, IconButton, Tooltip, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useEffect, useRef } from "react";
import { CSVLink } from "react-csv";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { useDebounce } from "react-use";
import { AccessEnum, SingleFilterValue } from "~/api/GeneratedApi";
import { useStyles } from "~/pages/fairway-maintenance-reporting-page/GenericSearchPage";
import { useAccess } from "~/utils/hooks/useAccess";
import { apiErrorMessage } from "~/utils/hooks/useApiErrorHandler";
import {
  getMaterialTableContent,
  useCopyToClipBoard,
} from "~/utils/hooks/useCopyToClipBoard";

const useStylesAddIcon = makeStyles((theme) => ({
  addIcon: {
    boxShadow: theme.shadows[3],
  },
}));

export interface QueryResult<T> {
  results: T[];
  currentPage: number;
  pageSize: number;
  totalCount: number;
}

export interface QueryOptions {
  page?: number | null | undefined;
  pageSize?: number | null | undefined;
  sortField?: string | null | undefined;
  descending?: boolean | undefined;
  search?: string | undefined;
  filters?: SingleFilterValue[] | null;
}

export type CustomColumn<T extends object> = Exclude<Column<T>, "field"> & {
  field?: Paths<T>;
};

export type CustomTableProps<T extends object> = Omit<
  Partial<MaterialTableProps<T>>,
  "actions"
> & {
  actions?:
    | (
        | CustomTableAction<T>
        | ((rowData: T) => CustomTableAction<T>)
        | {
            action: (rowData: T) => CustomTableAction<T>;
            position: string;
          }
      )[]
    | undefined;
};

export type CustomTableAction<T extends object> = (
  | (Omit<Action<T>, "onClick"> & {
      isLink: boolean;
      basePath: string | ((id: string) => string);
      //    onClick: (event: any, data: T | T[]) => void;
    })
  | Action<T>
) & { requiredAccess?: AccessEnum };

interface BaseProps<T extends object> {
  columns: CustomColumn<T>[];
  tableProps?: CustomTableProps<T>;
}

interface PropsFetch<T extends object> extends BaseProps<T> {
  fetch: (options: QueryOptions) => Promise<QueryResult<T>>;
  getRefetch?: (refetchFunction: () => void) => void;
  onSuccess?: (data: QueryResult<T>) => void;
}

interface ExtraProps<T extends Object> {
  showCopyButton?: boolean;
  showCsvExport?: boolean;
  getFilteredData?: (data: BookingTableData<T>[]) => void;
  csvData?: Partial<T>[];
  getTableRef?: (ref: MaterialTable<T>) => void;
}

interface PropsData<T extends object> extends BaseProps<T> {
  data: T[];
}

export type BookingTableData<T extends Object> = T & {
  tableData?: Record<string, string>;
};

const GenericTable = <T extends object>({
  tableProps,
  columns,
  showCopyButton = false,
  showCsvExport = false,
  csvData,
  getFilteredData,
  ...rest
}: (PropsFetch<T> | PropsData<T>) & ExtraProps<T>): JSX.Element => {
  const { t } = useTranslation("material-table");
  const classes = useStylesAddIcon();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tableRef = useRef<any>();
  const [tableData, setTableData] = React.useState<BookingTableData<T>[]>([]);

  // console.log("rest.data", rest.data);
  const restData = (rest as PropsData<T>)?.data;

  React.useEffect(() => {
    if (restData) setTableData(restData);
  }, [restData]);

  const [toolbar, setToolBar] = React.useState<HTMLDivElement | undefined>();

  const { accesses } = useAccess();
  const { handleCopyClick } = useCopyToClipBoard();

  const copyToClip = () => {
    const stringToCopy = getMaterialTableContent({ tableRef });
    handleCopyClick(stringToCopy);
  };

  useEffect(() => {
    if (!tableRef.current || !("getRefetch" in rest) || !rest.getRefetch)
      return;
    rest.getRefetch?.(() => tableRef.current.onQueryChange());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  }, [rest]);

  useEffect(() => {
    if (tableRef.current)
      rest?.getTableRef?.(tableRef?.current as MaterialTable<T>);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  }, [rest]);

  const checkedTableProps: CustomTableProps<T> | undefined =
    React.useMemo(() => {
      if (!tableProps) return tableProps;
      if (!tableProps.actions?.length) return tableProps;
      const filteredActions = tableProps.actions.filter((a) =>
        "requiredAccess" in a
          ? a.requiredAccess !== undefined && accesses[a.requiredAccess]
          : true,
      );
      return { ...tableProps, actions: filteredActions };
    }, [accesses, tableProps]);
  const buttonClass = useStyles();

  useDebounce(
    () => {
      getFilteredData?.(tableData);
    },
    100,
    [tableData],
  );

  return (
    <Box
      component="div"
      ref={(ref) => {
        const divRef = ref as HTMLDivElement | null;
        if (!divRef) return;
        const tb = divRef.querySelector(".MuiToolbar-root") as HTMLDivElement;
        setToolBar(tb);
      }}
    >
      <span>
        {toolbar && showCopyButton
          ? createPortal(
              <Tooltip title={t("copy-content-clipboard") ?? ""}>
                <IconButton onClick={copyToClip}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>,
              toolbar,
            )
          : null}
      </span>
      <span>
        {toolbar && "data" in rest && showCsvExport
          ? createPortal(
              <Box sx={{ display: "flex", marginX: "5px" }}>
                <CSVLink
                  filename="Kaluston varauslista"
                  data={csvData ?? rest.data}
                  separator=";"
                  className={buttonClass.buttonStyles}
                  uFEFF
                >
                  <Typography> {t("fairwayReport:csvExport")}</Typography>
                </CSVLink>
              </Box>,

              toolbar,
            )
          : null}
      </span>
      <MaterialTable<T>
        tableRef={tableRef}
        columns={columns}
        onFilterChange={() => {
          console.log(tableRef.current.state.data);
          setTableData(tableRef.current?.state?.data as BookingTableData<T>[]);
        }}
        onSearchChange={() => {
          setTableData(tableRef.current?.state?.data as BookingTableData<T>[]);
        }}
        data={
          "data" in rest
            ? rest.data
            : (query) => {
                // Transform the filters
                const filters: QueryOptions["filters"] = query.filters.map(
                  (f) => ({
                    field: f.column.field as string,
                    value: String(f.value),
                  }),
                );
                return new Promise((resolve, reject) => {
                  rest
                    .fetch({
                      page: query.page,
                      pageSize: query.pageSize,
                      sortField: query.orderBy?.field?.toString() ?? undefined,
                      descending: query.orderDirection === "desc",
                      search: query.search,
                      filters,
                    })
                    .then((result) => {
                      rest.onSuccess?.(result);
                      resolve({
                        data: result.results,
                        page: result.currentPage,
                        totalCount: result.totalCount,
                      });
                    })
                    .catch((e) => {
                      const messageOrKey = apiErrorMessage(e as Error);
                      reject(
                        "message" in messageOrKey
                          ? messageOrKey.message
                          : t(`apiResponses:${messageOrKey.key}`),
                      );
                    });
                });
              }
        }
        components={{
          Action: (props) => {
            // eslint-disable-next-line react/destructuring-assignment
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const { action, data } = props;

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            let curAction = action;

            if (typeof action?.action === "function") {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              curAction = action.action(data);
            }

            if (curAction?.icon === "add") {
              return (
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                <Tooltip title={curAction.tooltip}>
                  <Fab
                    onClick={(e: unknown) => curAction.onClick(e, data)}
                    color="secondary"
                    size="small"
                    classes={{ root: classes.addIcon }}
                  >
                    <AddIcon />
                  </Fab>
                </Tooltip>
              );
            }
            if (curAction.isLink && !curAction.disabled) {
              if ("onClick" in curAction) delete curAction.onClick;
              return (
                <Box
                  component={RouterLink}
                  style={{ color: "inherit" }}
                  to={`${curAction.basePath(data.id)}`}
                >
                  <MTableAction {...props} />
                </Box>
              );
            }
            return <MTableAction {...props} />;
          },
        }}
        localization={{
          header: {
            actions: t("actions"),
          },
          toolbar: {
            searchTooltip: t("search"),
            searchPlaceholder: t("search"),
          },
          pagination: {
            firstTooltip: t("first"),
            nextTooltip: t("next"),
            previousTooltip: t("previous"),
            lastTooltip: t("last"),
            labelRowsSelect: t("rows"),
            // labelRowsPerPage: t("rows-per-page"),
            labelRowsPerPage: "",
            labelDisplayedRows: "{from}-{to} / {count}",
          },
          body: {
            emptyDataSourceMessage: t("empty"),
          },
          error: t("error"),
        }}
        {...(checkedTableProps as Partial<MaterialTableProps<T>>)}
      />
    </Box>
  );
};

export default GenericTable;
